import React, { useState, useEffect } from "react";
import { formatCurrency } from "../utils/currencyUtils";
import noCarImageAvailable from "../images/no_car_image_available.png";
import {
  Check,
  Users,
  Briefcase,
  Zap,
  MapPin,
  Snowflake,
  ShieldCheck,
  Lock,
  Fuel,
} from "lucide-react";
import { GiCarDoor } from "react-icons/gi";
import { useCurrency } from "../context/CurrencyContext";

// ---------------------------------------------------------------------------
// Loading component
// ---------------------------------------------------------------------------
const PromotionalLoadingComponent = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const isSecurityPhase = progress < 35;

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => onComplete?.(), 600);
          return 100;
        }
        return prev + (prev < 35 ? 0.4 : 0.8);
      });
    }, 30);
    return () => clearInterval(progressInterval);
  }, [onComplete]);

  return (
    <div className="w-full py-16 px-6">
      <div className="max-w-4xl mx-auto flex flex-col items-center">
        <h2 className="text-xl md:text-2xl font-medium text-slate-700 mb-12">
          {isSecurityPhase
            ? "Establishing secure connection..."
            : "Searching for your perfect car at the best price"}
        </h2>
        <div className="w-full max-w-2xl relative mb-12">
          {isSecurityPhase && (
            <div className="absolute -top-12 left-1/2 -translate-x-1/2 animate-bounce">
              <div className="p-2 bg-slate-50 rounded-full border border-slate-100">
                <Lock className="w-6 h-6 text-primary" />
              </div>
            </div>
          )}
          <div className="h-6 w-full bg-slate-100 rounded-sm overflow-hidden p-1 shadow-inner">
            <div
              className="h-full bg-primary transition-all duration-300 ease-linear rounded-sm"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        <div className="w-full max-w-2xl grid grid-cols-1 md:grid-cols-2 gap-8 pt-8 border-t border-slate-100">
          <div className="flex flex-col items-center md:items-start space-y-3">
            <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
              Quick and secure payment
            </span>
            <div className="flex gap-4 grayscale opacity-70">
              {["VISA", "MC", "AMEX"].map((c) => (
                <div
                  key={c}
                  className="h-6 w-10 bg-slate-200 rounded text-[8px] flex items-center justify-center font-bold"
                >
                  {c}
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-center md:justify-end gap-4">
            <div className="text-right">
              <div className="text-xl font-black text-slate-800">100%</div>
              <div className="text-[10px] font-bold text-primary uppercase tracking-widest">
                Secure Transaction
              </div>
            </div>
            <div className="p-3 bg-primary/10 rounded-full">
              <ShieldCheck className="w-8 h-8 text-primary" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Spec pill
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const FUEL_POLICY_LABELS = {
  "FULL-FULL": "Full to full",
  "FULL_TO_FULL": "Full to full",
  "SAME-SAME": "Same-to-same",
  "SAME_TO_SAME": "Same-to-same",
};

const formatFuelPolicy = (policy) => {
  if (!policy) return null;
  const mapped = FUEL_POLICY_LABELS[String(policy).toUpperCase()];
  if (mapped) return mapped;
  const s = String(policy).replace(/_/g, " ").toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const plural = (n, word) => `${n} ${word}${Number(n) === 1 ? "" : "s"}`;

// One spec list for results + checkout. Reads only normalizeOffer fields.
// `label` is the long form (desktop pills), `short` is for compact mobile rows.
export const buildSpecs = (offer) => {
  const fuel = formatFuelPolicy(offer.fuelPolicy);
  const ac = offer.airCond ? "A/C" : "Non-A/C";

  return [
    offer.doors && { icon: GiCarDoor, title: "Doors", label: plural(offer.doors, "door"), short: offer.doors },
    offer.seats && { icon: Users, title: "Seats", label: plural(offer.seats, "seat"), short: offer.seats },
    offer.bags && { icon: Briefcase, title: "Bags", label: plural(offer.bags, "bag"), short: offer.bags },
    offer.transmission && { icon: Zap, title: "Transmission", label: offer.transmission, short: offer.transmission },
    { icon: Snowflake, title: "Air conditioning", label: ac, short: ac },
    fuel && { icon: Fuel, title: "Fuel policy", label: fuel, short: fuel },
  ].filter(Boolean);
};

// ---------------------------------------------------------------------------
// Spec pill
// ---------------------------------------------------------------------------
export const SpecPill = ({ icon: Icon, label, title }) => (
  <div
    title={title}
    className="flex items-center gap-1 border border-surface-border rounded-md px-1.5 py-0.5 text-[11px] font-semibold font-body text-dark bg-white whitespace-nowrap"
  >
    <Icon className="w-3.5 h-3.5 shrink-0 text-dark" />
    <span>{label}</span>
  </div>
);

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------
export const CategoryBadge = ({ label }) =>
  label ? (
    <span className="inline-block bg-primary text-white text-xs font-bold font-heading px-2.5 py-1 rounded-md">
      {label}
    </span>
  ) : null;

export const IncludedFeatures = ({ items, columns = 2 }) =>
  items.length > 0 ? (
    <div className="rounded-xl border border-surface-border bg-slate-50 px-4 py-3">
      <p className="text-[10px] uppercase tracking-wider font-heading font-bold text-text-muted mb-2">
        Included features
      </p>
      <div
        className={`grid gap-x-8 gap-y-1.5 ${
          columns === 2 ? "grid-cols-[max-content_max-content]" : "grid-cols-1"
        }`}
      >
        {items.map((inc, i) => (
          <div
            key={i}
            className={`flex items-start gap-2 text-[13px] font-medium text-dark font-body leading-snug ${
              columns === 2 ? "whitespace-nowrap" : ""
            }`}
          >
            <Check className="w-3.5 h-3.5 shrink-0 text-primary mt-0.5" />
            <span>{inc}</span>
          </div>
        ))}
      </div>
    </div>
  ) : null;

const TripLocations = ({ pickup, dropoff, pickupType }) => {
  const isSame =
    (pickup || "").trim().toLowerCase() === (dropoff || "").trim().toLowerCase();

  const block = (label, place, type) => (
    <div className="flex items-start gap-1.5 min-w-0">
      <MapPin className="w-4 h-4 text-primary shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="font-bold text-primary">{label}</p>
        <p className="font-medium text-dark">
          {place}
          {type && (
            <span className="ml-1.5 text-xs font-normal text-text-muted whitespace-nowrap">
              · {type}
            </span>
          )}
        </p>
      </div>
    </div>
  );

  return (
    <div className="flex flex-wrap gap-x-8 gap-y-2 text-[13px] font-body">
      {block(isSame ? "Pick-up & Drop-off" : "Pick-up", pickup, pickupType)}
      {!isSame && block("Drop-off", dropoff)}
    </div>
  );
};

const PaySplit = ({ payNowText, payAtPickupText }) => (
  <div className="grid grid-cols-2 w-full rounded-xl border border-surface-border overflow-hidden bg-white">
    <div className="bg-primary/10 px-2.5 py-2.5">
      <p className="text-[10px] uppercase tracking-wider font-heading font-bold text-primary">
        Pay now
      </p>
      <p className="text-sm font-extrabold text-primary font-heading whitespace-nowrap">
        {payNowText}
      </p>
    </div>
    <div className="px-2.5 py-2.5 text-right">
      <p className="text-[10px] uppercase tracking-wider font-heading font-bold text-text-muted">
        Pay at pickup
      </p>
      <p className="text-sm font-extrabold text-dark font-heading whitespace-nowrap">
        {payAtPickupText}
      </p>
    </div>
  </div>
);

const ConfirmationNote = ({ instant }) => (
  <div className="flex items-center gap-1.5 justify-center w-full">
    <Check className="w-3.5 h-3.5 text-primary shrink-0" />
    <span className="text-xs text-text-muted font-body">
      {instant ? "Instant confirmation" : "Standard confirmation"}
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// CarCard
// ---------------------------------------------------------------------------
const CarCard = ({
  vehicle,
  currencyCode,
  searchId,
  pickupLocationName,
  dropoffLocationName,
}) => {
  const offer = vehicle.primaryOffer;
  const { selectedCurrency } = useCurrency();
  if (!offer) return null;
  // All prices come straight from normalizeOffer (resultsPipeline.js).
  // No calculations here — only formatting.
  const currency = { code: currencyCode };
  const totalText = formatCurrency(offer.totalPrice, currency);
  const payNowText = formatCurrency(offer.payNowPrice, currency);
  const payAtPickupText = formatCurrency(offer.payAtPickupPrice, currency);
  const hasPayAtPickup = offer.payAtPickupPrice > 0;
  const pickupLocationType = offer.pickupType;

  const handleBookNow = () => {
    const hash = offer.offerId || offer.offer_hash || offer.offerHash;
    localStorage.setItem(
      "originalSearchURL",
      window.location.pathname + window.location.search,
    );
    localStorage.setItem("search_id_currency", selectedCurrency?.code || "USD");
    const url = `/checkout?search_id=${encodeURIComponent(searchId || "")}&carOfferHash=${encodeURIComponent(hash || "")}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const firstOffer = vehicle.offers?.[0] || {};
  const fuelLabel = formatFuelPolicy(offer.fuelPolicy);

  const specs = buildSpecs(offer);

    const mobileSpecs = [
    firstOffer.doors && { icon: GiCarDoor, label: firstOffer.doors, title: "Doors" },
    vehicle.seats && { icon: Users, label: vehicle.seats, title: "Seats" },
    vehicle.bags && { icon: Briefcase, label: vehicle.bags, title: "Bags" },
    vehicle.transmission && { icon: Zap, label: vehicle.transmission, title: "Transmission" },
    { icon: Snowflake, label: firstOffer.airCond ? "A/C" : "Non-A/C", title: "Air conditioning" },
    fuelLabel && { icon: Fuel, label: fuelLabel, title: "Fuel policy" },
  ].filter(Boolean);

  const sameLocation =
    (pickupLocationName || "").trim().toLowerCase() ===
    (dropoffLocationName || "").trim().toLowerCase();

  const inclusions = offer.inclusions || [];

  const supplierBlock = (logoClass) => (
    <div className="flex flex-col items-center gap-1">
      {vehicle.logoUrl ? (
        <img src={vehicle.logoUrl} alt="" className={logoClass} />
      ) : (
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary border border-primary/20">
          {vehicle.name?.slice(0, 2).toUpperCase()}
        </div>
      )}
      <span className="text-xs font-medium text-dark font-body text-center">
        {vehicle.name}
      </span>
    </div>
  );

  return (
    <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      {/* ── MOBILE LAYOUT ── */}
      <div className="lg:hidden flex flex-col">
        <div className="p-3 flex flex-col gap-2.5">
          {/* Header: image + badge/title/supplier */}
          <div className="flex gap-3">
            <div className="w-28 shrink-0 flex flex-col items-start gap-1">
              {vehicle.childType && (
                <span className="bg-primary text-white text-[9px] font-bold font-heading px-1.5 py-0.5 rounded leading-none">
                  {vehicle.childType}
                </span>
              )}
              <img
                src={vehicle.imageUrl || noCarImageAvailable}
                alt={vehicle.model}
                className="h-16 w-full object-contain soft-edge-mask"
              />
            </div>
            <div className="min-w-0 flex-1 flex flex-col justify-center">
              <h3 className="text-[15px] font-extrabold text-dark font-heading leading-tight">
                {vehicle.model}{" "}
                <span className="text-[11px] font-normal text-text-muted">or similar</span>
              </h3>
              <div className="mt-1 flex items-center gap-1.5 min-w-0">
                {vehicle.logoUrl ? (
                  <img src={vehicle.logoUrl} alt="" className="h-5 max-w-[64px] object-contain" />
                ) : (
                  <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-[8px] font-bold text-primary border border-primary/20">
                    {vehicle.name?.slice(0, 2).toUpperCase()}
                  </span>
                )}
                <span className="text-[11px] text-text-muted font-body truncate">
                  {vehicle.name}
                </span>
              </div>
            </div>
          </div>

          {/* Specs: single line, swipe if it overflows */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1"> 
              {specs.map((s, i) => (
              <span
                key={i}
                title={s.title}
                className="flex items-center gap-1 text-[11px] font-semibold text-dark font-body whitespace-nowrap"
              >
                <s.icon className="w-3.5 h-3.5 shrink-0 text-text-muted" />
                {s.short}
              </span>
            ))}
          </div>

          {/* Features: compact 2-column list */}
          {inclusions.length > 0 && (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
              {inclusions.map((inc, i) => (
                <div
                  key={i}
                  className="flex items-start gap-1 min-w-0 text-[11px] text-dark font-body leading-tight"
                >
                  <Check className="w-3 h-3 shrink-0 text-primary mt-px" />
                  <span className="break-words min-w-0">{inc}</span>
                </div>
              ))}
            </div>
          )}

          {/* Locations */}
          <div className="flex flex-col gap-1.5 text-[11px] font-body">
            <div className="flex items-start gap-1">
              <MapPin className="w-3.5 h-3.5 text-primary shrink-0 mt-px" />
              <div className="min-w-0">
                <p className="font-bold text-primary">
                  {sameLocation ? "Pick-up & Drop-off" : "Pick-up"}
                </p>
                <p className="text-dark">
                  {pickupLocationName}
                  {pickupLocationType && (
                    <span className="text-text-muted whitespace-nowrap"> · {pickupLocationType}</span>
                  )}
                </p>
              </div>
            </div>
            {!sameLocation && (
              <div className="flex items-start gap-1">
                <MapPin className="w-3.5 h-3.5 text-primary shrink-0 mt-px" />
                <div className="min-w-0">
                  <p className="font-bold text-primary">Drop-off</p>
                  <p className="text-dark">{dropoffLocationName}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Price bar */}
        <div className="bg-primary/5 border-t border-surface-border px-3 py-2.5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-extrabold text-dark font-heading leading-none">
                {totalText}
              </span>
              <span className="text-[10px] text-text-muted font-body">total</span>
            </div>
            <p className="mt-0.5 text-[11px] text-text-muted font-body leading-tight">
              Pay now <span className="font-bold text-primary">{payNowText}</span>
              {hasPayAtPickup && <> · {payAtPickupText} at pickup</>}
            </p>
          </div>
          <div className="shrink-0 flex flex-col items-center gap-1">
            <button
              onClick={handleBookNow}
              className="bg-primary hover:bg-primary-hover text-white font-bold font-heading py-2.5 px-5 rounded-xl transition-colors text-xs tracking-widest uppercase shadow-primary-glow"
            >
              Reserve
            </button>
            <p className="flex items-center gap-1 text-[10px] text-text-muted font-body whitespace-nowrap">
              <Check className="w-3 h-3 text-primary shrink-0" />
              {offer.instantConfirmation ? "Instant" : "Standard"} confirmation
            </p>
          </div>
        </div>
      </div>

      {/* ── DESKTOP LAYOUT ── */}
      <div className="hidden lg:flex flex-row">
        {/* Left: badge + image + supplier */}
        <div className="w-40 xl:w-52 shrink-0 flex flex-col p-4 border-r border-surface-border">
          <div>
            <CategoryBadge label={vehicle.childType} />
          </div>
          <div className="flex-1 flex items-center justify-center py-3">
            <img
              src={vehicle.imageUrl || noCarImageAvailable}
              alt={vehicle.model}
              className="h-32 object-contain w-full soft-edge-mask"
            />
          </div>
          {supplierBlock("h-10 object-contain max-w-[120px]")}
        </div>

        {/* Middle: details */}
        <div className="flex-1 min-w-0 p-4 flex flex-col gap-3">
          <h3 className="text-xl font-extrabold text-dark font-heading">
            {vehicle.model}{" "}
            <span className="text-sm font-normal text-text-muted">or similar</span>
          </h3>

          <div className="flex flex-wrap gap-1.5">
            {specs.map((s, i) => (
              <SpecPill key={i} icon={s.icon} label={s.label} title={s.title} />
            ))}
          </div>

          <IncludedFeatures items={inclusions} columns={2} />

          <div className="mt-auto pt-1">
            <TripLocations
              pickup={pickupLocationName}
              dropoff={dropoffLocationName}
              pickupType={pickupLocationType}
            />
          </div>
        </div>

        {/* Right: pricing */}
        <div className="w-56 xl:w-60 shrink-0 p-4 flex flex-col justify-center gap-4 border-l border-surface-border bg-primary/5">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-heading font-bold text-text-muted">
                Total price
              </p>
              <p className="text-[11px] text-text-muted font-body">incl. taxes & fees</p>
            </div>
            <p className="text-2xl font-extrabold text-dark font-heading leading-tight">
              {totalText}
            </p>
          </div>

          {hasPayAtPickup ? (
            <PaySplit payNowText={payNowText} payAtPickupText={payAtPickupText} />
          ) : (
            <div className="w-full rounded-xl bg-primary/10 px-3 py-2 flex items-center justify-between gap-2">
              <p className="text-[10px] uppercase tracking-wider font-heading font-bold text-primary">
                Pay now
              </p>
              <p className="text-sm font-extrabold text-primary font-heading whitespace-nowrap">
                {payNowText}
              </p>
            </div>
          )}

          <button
            onClick={handleBookNow}
            className="w-full bg-primary hover:bg-primary-hover text-white font-bold font-heading py-3.5 rounded-xl transition-colors text-sm tracking-widest uppercase shadow-primary-glow"
          >
            Reserve
          </button>

          <ConfirmationNote instant={offer.instantConfirmation} />
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// CarDetails
// ---------------------------------------------------------------------------
const CarDetails = ({
  vehicleResults = [],
  isLoading = false,
  carDataProcessed = false,
  currencyCode = "USD",
  searchId,
  pickupLocationName,
  dropoffLocationName,
  onSelectOffer,
}) => {
  if (isLoading && !carDataProcessed) {
    return <PromotionalLoadingComponent />;
  }

  return (
    <div className="space-y-4 w-full">
      {vehicleResults.map((vehicle, index) => (
        <CarCard
          key={vehicle.vehicleId || vehicle.id || `${vehicle.model}-${index}`}
          vehicle={vehicle}
          currencyCode={currencyCode}
          searchId={searchId}
          pickupLocationName={pickupLocationName}
          dropoffLocationName={dropoffLocationName}
          index={index}
        />
      ))}
    </div>
  );
};

export default CarDetails;
